// src/pages/SuperAdmin.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  FormControl,    // Add this
  InputLabel,     // Add this
  Select,         // Add this
  MenuItem        // Add this
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc, 
  deleteDoc,
  query,
  where,
  serverTimestamp,
  onSnapshot,
  orderBy
} from 'firebase/firestore';
import { getAuth, updatePassword, sendPasswordResetEmail } from 'firebase/auth';
import Swal from 'sweetalert2';
import './SuperAdmin.css';

const SuperAdmin = () => {
  const { currentUser, userData, isAdmin } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
    userId: '',
    userEmail: ''
  });
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    fullName: '',
    phone: '',
    role: 'admin',
    department: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Check if current user is super admin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      if (!currentUser) return;
      
      try {
        const adminDoc = await getDoc(doc(db, 'admins', currentUser.uid));
        if (adminDoc.exists() && adminDoc.data().isSuperAdmin === true) {
          setIsSuperAdmin(true);
          loadData();
        } else {
          setIsSuperAdmin(false);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking super admin:', error);
        setIsSuperAdmin(false);
        setLoading(false);
      }
    };

    checkSuperAdmin();
  }, [currentUser]);

  const loadData = () => {
    loadAdmins();
    loadUsers();
  };

  const loadAdmins = () => {
    const adminsQuery = query(
      collection(db, 'admins'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(adminsQuery, (snapshot) => {
      const adminsData = [];
      snapshot.forEach(doc => {
        adminsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setAdmins(adminsData);
      setLoading(false);
    }, (error) => {
      console.error('Error loading admins:', error);
      showSnackbar('Error loading admins', 'error');
      setLoading(false);
    });

    return unsubscribe;
  };

  const loadUsers = () => {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('fullName', 'asc')
    );

    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const usersData = [];
      snapshot.forEach(doc => {
        usersData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setUsers(usersData);
    }, (error) => {
      console.error('Error loading users:', error);
    });

    return unsubscribe;
  };

  // Check if user exists in Firebase Auth
  const checkUserInAuth = async (email) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        return false;
      }
      return true;
    }
  };

  // Create new admin user in Firebase Auth
  const createAdminUser = async (email, password, userData) => {
    try {
      const response = await fetch('/.netlify/functions/create-admin-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          userData: {
            ...userData,
            isSuperAdmin: userData.role === 'super'
          }
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Error creating admin user:', error);
      throw error;
    }
  };

  // Reset password via Admin SDK (Netlify Function)
  const resetUserPassword = async (userId, newPassword) => {
    try {
      const response = await fetch('/.netlify/functions/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          newPassword
        })
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  const handleResetPassword = async () => {
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      showSnackbar('Please fill in all password fields', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showSnackbar('Password must be at least 6 characters', 'error');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showSnackbar('Passwords do not match', 'error');
      return;
    }

    setLoading(true);

    try {
      await resetUserPassword(passwordData.userId, passwordData.newPassword);
      
      showSnackbar('Password reset successfully!', 'success');
      setShowPasswordModal(false);
      setPasswordData({
        newPassword: '',
        confirmPassword: '',
        userId: '',
        userEmail: ''
      });
    } catch (error) {
      console.error('Error resetting password:', error);
      showSnackbar('Failed to reset password: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminData.email || !newAdminData.fullName || !newAdminData.password) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    if (newAdminData.password.length < 6) {
      showSnackbar('Password must be at least 6 characters', 'error');
      return;
    }

    if (!newAdminData.email.includes('@')) {
      showSnackbar('Please enter a valid email address', 'error');
      return;
    }

    setLoading(true);

    try {
      const result = await createAdminUser(
        newAdminData.email,
        newAdminData.password,
        {
          fullName: newAdminData.fullName,
          phone: newAdminData.phone,
          department: newAdminData.department,
          role: newAdminData.role,
          isSuperAdmin: newAdminData.role === 'super'
        }
      );

      showSnackbar(`Admin ${newAdminData.fullName} added successfully!`, 'success');
      setShowAddAdminModal(false);
      setNewAdminData({
        email: '',
        fullName: '',
        phone: '',
        role: 'admin',
        department: '',
        password: ''
      });
    } catch (error) {
      console.error('Error adding admin:', error);
      showSnackbar('Failed to add admin: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminName) => {
    const result = await Swal.fire({
      title: 'Delete Admin?',
      text: `Are you sure you want to remove ${adminName} as an admin?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'admins', adminId));
        showSnackbar('Admin removed successfully!', 'success');
      } catch (error) {
        console.error('Error deleting admin:', error);
        showSnackbar('Failed to delete admin', 'error');
      }
    }
  };

  const handleToggleSuperAdmin = async (adminId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'admins', adminId), {
        isSuperAdmin: !currentStatus,
        updatedAt: serverTimestamp()
      });
      showSnackbar('Super admin status updated!', 'success');
    } catch (error) {
      console.error('Error updating super admin status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getAdminRoleLabel = (admin) => {
    if (admin.isSuperAdmin) return 'Super Admin';
    return admin.role || 'Admin';
  };

  const getAdminRoleColor = (admin) => {
    if (admin.isSuperAdmin) return 'error';
    return 'primary';
  };

  const filteredUsers = users.filter(user =>
    user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 5 }}>
        <Alert severity="error" icon={<SecurityIcon />}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography variant="body2">
            You need super administrator privileges to access this page.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (loading && admins.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="super-admin-page" sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box className="super-admin-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#1034A6', fontWeight: 'bold' }}>
            <AdminIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Super Admin Panel
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage administrators and user accounts
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setShowAddAdminModal(true)}
            sx={{ bgcolor: '#1034A6' }}
          >
            Add Admin
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" color="textSecondary">Total Admins</Typography>
                  <Typography variant="h3">{admins.length}</Typography>
                </Box>
                <AdminIcon sx={{ fontSize: 40, color: '#1034A6', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" color="textSecondary">Super Admins</Typography>
                  <Typography variant="h3">{admins.filter(a => a.isSuperAdmin).length}</Typography>
                </Box>
                <VerifiedUserIcon sx={{ fontSize: 40, color: '#4caf50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" color="textSecondary">Regular Admins</Typography>
                  <Typography variant="h3">{admins.filter(a => !a.isSuperAdmin).length}</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: '#ff9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" color="textSecondary">Total Users</Typography>
                  <Typography variant="h3">{users.length}</Typography>
                </Box>
                <SecurityIcon sx={{ fontSize: 40, color: '#9c27b0', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<AdminIcon />} label="Admin Management" />
        <Tab icon={<PeopleIcon />} label="User Management" />
      </Tabs>

      {/* Admin Management Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Administrators</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {admins
                    .filter(admin =>
                      admin.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      admin.email?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map(admin => (
                      <TableRow key={admin.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ bgcolor: admin.isSuperAdmin ? '#c62828' : '#1034A6' }}>
                              {admin.fullName?.charAt(0) || 'A'}
                            </Avatar>
                            {admin.fullName}
                          </Box>
                        </TableCell>
                        <TableCell>{admin.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={getAdminRoleLabel(admin)}
                            color={getAdminRoleColor(admin)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{admin.department || 'N/A'}</TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={admin.isSuperAdmin || false}
                                onChange={() => handleToggleSuperAdmin(admin.id, admin.isSuperAdmin)}
                                color="error"
                                disabled={admin.id === currentUser.uid}
                              />
                            }
                            label={admin.isSuperAdmin ? 'Super Admin' : 'Admin'}
                            labelPlacement="start"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Reset Password">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setPasswordData({
                                  ...passwordData,
                                  userId: admin.id,
                                  userEmail: admin.email
                                });
                                setShowPasswordModal(true);
                              }}
                              sx={{ color: '#ff9800' }}
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                          {admin.id !== currentUser.uid && (
                            <Tooltip title="Remove Admin">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAdmin(admin.id, admin.fullName)}
                                sx={{ color: '#dc3545' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* User Management Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Users</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Year Group</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map(user => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar>{user.fullName?.charAt(0) || 'U'}</Avatar>
                          {user.fullName || user.firstName || 'Unknown'}
                        </Box>
                      </TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>{user.phone || 'N/A'}</TableCell>
                      <TableCell>{user.yearName || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.status || 'Active'}
                          color={user.status === 'inactive' ? 'default' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Reset Password">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPasswordData({
                                ...passwordData,
                                userId: user.id,
                                userEmail: user.email
                              });
                              setShowPasswordModal(true);
                            }}
                            sx={{ color: '#ff9800' }}
                          >
                            <LockResetIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Reset Password Modal */}
      <Dialog open={showPasswordModal} onClose={() => setShowPasswordModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockResetIcon color="warning" />
            Reset Password
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Resetting password for: <strong>{passwordData.userEmail}</strong>
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              helperText="Password must be at least 6 characters"
            />
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Confirm Password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={loading}
            sx={{ bgcolor: '#ff9800' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Admin Modal */}
      <Dialog open={showAddAdminModal} onClose={() => setShowAddAdminModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon color="primary" />
            Add New Admin
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name *"
              value={newAdminData.fullName}
              onChange={(e) => setNewAdminData({ ...newAdminData, fullName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={newAdminData.email}
              onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
            />
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Password *"
              value={newAdminData.password}
              onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              helperText="Must be at least 6 characters"
            />
            <TextField
              fullWidth
              label="Phone (Optional)"
              value={newAdminData.phone}
              onChange={(e) => setNewAdminData({ ...newAdminData, phone: e.target.value })}
            />
            <TextField
              fullWidth
              label="Department (Optional)"
              value={newAdminData.department}
              onChange={(e) => setNewAdminData({ ...newAdminData, department: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newAdminData.role}
                label="Role"
                onChange={(e) => setNewAdminData({ ...newAdminData, role: e.target.value })}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super">Super Admin</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddAdminModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddAdmin}
            disabled={loading}
            sx={{ bgcolor: '#1034A6' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdmin;
